// CombinedRevenueTimelineChart component - Combined timeline chart for total revenue with year comparison

import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  formatDateShort,
  formatNumber,
  formatCurrency,
} from "../../utils/formatters";
import { getTimelineChart } from "../../services/api";
import { YEAR_COLORS } from "../../constants";

const CombinedRevenueTimelineChart = ({ data, filters }) => {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  // Toggle states for data types
  const [showRegular, setShowRegular] = useState(true);
  const [showDaypass, setShowDaypass] = useState(true);

  // Get available years from timelineData (from API)
  useEffect(() => {
    if (timelineData && timelineData.length > 0) {
      const years = [
        ...new Set(
          timelineData
            .map((item) => item.year)
            .filter(
              (year) => year && !isNaN(year) && year > 2000 && year < 2100,
            ),
        ),
      ].sort((a, b) => b - a);

      setAvailableYears(years);

      // Select last 2 years by default (only on first load)
      if (years.length > 0 && selectedYears.length === 0) {
        setSelectedYears(years.slice(0, Math.min(2, years.length)));
      }
    }
  }, [timelineData]);

  // Fetch timeline data from API
  useEffect(() => {
    const fetchTimelineData = async () => {
      setLoading(true);
      try {
        const effectiveStartDate = filters?.startDate || null;
        const effectiveEndDate = filters?.endDate || null;

        const result = await getTimelineChart(
          effectiveStartDate,
          effectiveEndDate,
          filters?.busLine,
          filters?.selectedYears && filters.selectedYears.length > 0
            ? filters.selectedYears.join(",")
            : null,
        );

        if (result.success && result.data) {
          // Map API data to chart format - combine passengers and daypass
          const chartData = result.data.map((item) => {
            const dateObj = new Date(item.date);
            let year;
            if (typeof item.date === "string" && item.date.includes("T")) {
              year = parseInt(item.date.substring(0, 4), 10);
            } else if (
              typeof item.date === "string" &&
              item.date.includes("-")
            ) {
              const dateParts = item.date.split("-");
              year =
                dateParts[0].length === 4
                  ? parseInt(dateParts[0], 10)
                  : dateObj.getFullYear();
            } else {
              year = dateObj.getFullYear();
            }

            const income = item.income || 0;
            const totalIncome = item.total_income || 0;
            const daypassRevenue = item.daypass_income || 0;
            const revenue = income - daypassRevenue; // ตั๋วฉีก = income - daypass_income
            const total = income; // รวมคือ income (ตั๋วฉีก + daypass)

            // Debug log
            // if (Math.random() < 0.05) {
            //   // Log 5% ของข้อมูล
            //   console.log("Revenue Data:", {
            //     date: item.date,
            //     income: income,
            //     total_income: totalIncome,
            //     daypass_income: daypassRevenue,
            //     calculated_revenue: revenue,
            //     total: total,
            //   });
            // }

            return {
              date: formatDateShort(item.date),
              fullDate: item.date,
              revenue: revenue, // รายรับตั๋วฉีกอย่างเดียว
              daypassRevenue: daypassRevenue,
              total: total, // รายรับรวมทั้งหมด
              year: year,
              monthDay: dateObj.toLocaleDateString("th-TH", {
                month: "short",
                day: "numeric",
              }),
            };
          });

          setTimelineData(chartData);
        }
      } catch (error) {
        console.error("Error fetching timeline data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimelineData();
  }, [filters]);

  // Prepare comparison data
  useEffect(() => {
    if (!timelineData || timelineData.length === 0) {
      setComparisonData([]);
      return;
    }

    // Group data by month-day
    const groupedByDate = timelineData.reduce((acc, item) => {
      const key = item.monthDay;
      if (!acc[key]) {
        acc[key] = {};
      }
      acc[key][item.year] = {
        revenue: item.revenue, // รายรับตั๋วฉีกเฉพาะ
        daypassRevenue: item.daypassRevenue,
        total: item.total,
      };
      return acc;
    }, {});

    // Convert to array format for chart
    const chartData = Object.entries(groupedByDate).map(([monthDay, years]) => {
      const dataPoint = { monthDay };
      selectedYears.forEach((year) => {
        if (years[year]) {
          dataPoint[`revenue_${year}`] = years[year].revenue;
          dataPoint[`daypassRevenue_${year}`] = years[year].daypassRevenue;
          dataPoint[`total_${year}`] = years[year].total;
        }
      });
      return dataPoint;
    });

    // Debug log
    // console.log("Comparison Data Sample:", chartData.slice(0, 3));
    // console.log("Timeline Data Sample:", timelineData.slice(0, 3));

    setComparisonData(chartData);
  }, [timelineData, selectedYears]);

  const handleYearToggle = (year) => {
    setSelectedYears((prev) => {
      if (prev.includes(year)) {
        return prev.filter((y) => y !== year);
      } else {
        return [...prev, year].sort((a, b) => b - a);
      }
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="bg-white p-4 border-2 border-blue-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          {payload.map((entry, index) => {
            const parts = entry.dataKey.split("_");
            const type = parts[0];
            const year = parts[1];
            let label = "";
            let color = entry.color;

            if (showRegular && showDaypass) {
              // Show combined total
              if (type === "total") {
                label = `รวมทั้งหมด ${year}`;
              }
            } else if (showRegular && !showDaypass) {
              // Show only regular
              if (type === "revenue") {
                label = `ตั๋วฉีก ${year}`;
              }
            } else if (!showRegular && showDaypass) {
              // Show only daypass
              if (type === "daypassRevenue") {
                label = `Daypass ${year}`;
              }
            }

            if (!label) return null;

            return (
              <p key={index} style={{ color }}>
                {label}: {formatCurrency(entry.value)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 border-2 border-blue-100">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!timelineData || timelineData.length === 0) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 border-2 border-green-100">
        <h3 className="text-xl font-bold text-green-800 mb-4">
          กราฟ Timeline รายรับรายวัน (รวมทุกประเภท)
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          ไม่มีข้อมูล
        </div>
      </div>
    );
  }

  // Calculate which dataKey to use based on toggle states
  const getDataKey = (year) => {
    if (showRegular && showDaypass) {
      return `total_${year}`;
    } else if (showRegular && !showDaypass) {
      return `revenue_${year}`;
    } else if (!showRegular && showDaypass) {
      return `daypassRevenue_${year}`;
    }
    return null;
  };

  const getLineName = (year) => {
    if (showRegular && showDaypass) {
      return `รวมทั้งหมด ${year}`;
    } else if (showRegular && !showDaypass) {
      return `ตั๋วฉีก ${year}`;
    } else if (!showRegular && showDaypass) {
      return `Daypass ${year}`;
    }
    return "";
  };

  // Render year comparison view
  if (selectedYears.length > 0 && comparisonData.length > 0) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 border-2 border-green-100 hover:border-green-200 transition-all duration-300">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
                กราฟ Timeline รายรับรายวัน
                <span className="px-2 py-0.5 text-xs font-bold bg-green-200 text-green-700 rounded-full">
                  {comparisonData.length} วัน
                </span>
              </h3>
              <p className="text-sm text-green-600">
                {!filters?.startDate && !filters?.endDate
                  ? `เปรียบเทียบข้อมูลปี ${selectedYears.join(", ")}`
                  : `ช่วงวันที่: ${filters.startDate} ถึง ${filters.endDate} (ปี ${selectedYears.join(", ")})`}
              </p>
            </div>

            {/* Toggle Buttons for Data Types */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowRegular(!showRegular)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                  showRegular
                    ? "text-white shadow-lg"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
                style={
                  showRegular
                    ? {
                        background:
                          showRegular && showDaypass
                            ? "linear-gradient(135deg, #10b981 0%, #14b8a6 100%)"
                            : "#22c55e",
                      }
                    : {}
                }
              >
                {showRegular ? "✓" : ""} ตั๋วฉีก
              </button>
              <button
                onClick={() => setShowDaypass(!showDaypass)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                  showDaypass
                    ? "text-white shadow-lg"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
                style={
                  showDaypass
                    ? {
                        background:
                          showRegular && showDaypass
                            ? "linear-gradient(135deg, #10b981 0%, #14b8a6 100%)"
                            : "#14b8a6",
                      }
                    : {}
                }
              >
                {showDaypass ? "✓" : ""} Daypass
              </button>
            </div>
          </div>

          {/* Year Selection */}
          <div className="flex flex-wrap gap-2 mb-4">
            {availableYears.map((year, index) => (
              <button
                key={year}
                onClick={() => handleYearToggle(year)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  selectedYears.includes(year)
                    ? "text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                style={
                  selectedYears.includes(year)
                    ? { background: YEAR_COLORS[index % YEAR_COLORS.length] }
                    : {}
                }
              >
                {year} {selectedYears.includes(year) && "✓"}
              </button>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={comparisonData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="monthDay"
              tick={{ fill: "#666", fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fill: "#666", fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: "20px" }} />

            {/* Lines for each selected year */}
            {selectedYears.map((year, index) => {
              const dataKey = getDataKey(year);
              if (!dataKey) return null;

              return (
                <Line
                  key={dataKey}
                  type="monotone"
                  dataKey={dataKey}
                  name={getLineName(year)}
                  stroke={YEAR_COLORS[index % YEAR_COLORS.length]}
                  strokeWidth={3}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {selectedYears.map((year) => {
            const yearData = timelineData.filter((item) => item.year === year);
            const totalRevenue = yearData.reduce(
              (sum, item) => sum + (item.revenue || 0),
              0,
            );
            const totalDaypassRevenue = yearData.reduce(
              (sum, item) => sum + (item.daypassRevenue || 0),
              0,
            );
            const total = totalRevenue + totalDaypassRevenue;
            const avgPerDay = yearData.length > 0 ? total / yearData.length : 0;

            // Find max and min revenue days
            let maxDay = null;
            let minDay = null;
            if (yearData.length > 0) {
              maxDay = yearData.reduce(
                (max, item) => (item.total > max.total ? item : max),
                yearData[0],
              );
              minDay = yearData.reduce(
                (min, item) => (item.total < min.total ? item : min),
                yearData[0],
              );
            }

            return (
              <div
                key={year}
                className="bg-white rounded-xl p-4 border-2 border-green-100 hover:border-green-300 transition-all"
              >
                <p className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">
                  📊 ปี {year}
                </p>

                {showRegular && showDaypass && (
                  <>
                    <p className="text-xs text-green-600 mb-1">
                      📈 เฉลี่ย: {formatCurrency(Math.round(avgPerDay))}/วัน
                    </p>
                    {maxDay && (
                      <div className="text-xs text-emerald-700 mb-1">
                        🔝 สูงสุด: {formatCurrency(Math.round(maxDay.total))}
                        <br />
                        <span className="text-gray-500 ml-3">
                          ({maxDay.date})
                        </span>
                      </div>
                    )}
                    {minDay && (
                      <div className="text-xs text-teal-600 mb-1">
                        🔻 ต่ำสุด: {formatCurrency(Math.round(minDay.total))}
                        <br />
                        <span className="text-gray-500 ml-3">
                          ({minDay.date})
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-gray-600 mt-2">
                      <span className="text-green-600">ตั๋วฉีก:</span>{" "}
                      {formatCurrency(
                        Math.round(totalRevenue / yearData.length),
                      )}
                      /วัน
                      <br />
                      <span className="text-emerald-600">Daypass:</span>{" "}
                      {formatCurrency(
                        Math.round(totalDaypassRevenue / yearData.length),
                      )}
                      /วัน
                    </div>
                  </>
                )}

                {showRegular && !showDaypass && (
                  <>
                    <p className="text-xs text-green-600 mb-1">
                      📈 เฉลี่ย:{" "}
                      {formatCurrency(
                        Math.round(totalRevenue / yearData.length),
                      )}
                      /วัน
                    </p>
                    {maxDay && (
                      <div className="text-xs text-emerald-700 mb-1">
                        🔝 สูงสุด: {formatCurrency(Math.round(maxDay.revenue))}
                        <br />
                        <span className="text-gray-500 ml-3">
                          ({maxDay.date})
                        </span>
                      </div>
                    )}
                    {minDay && (
                      <div className="text-xs text-teal-600">
                        🔻 ต่ำสุด: {formatCurrency(Math.round(minDay.revenue))}
                        <br />
                        <span className="text-gray-500 ml-3">
                          ({minDay.date})
                        </span>
                      </div>
                    )}
                  </>
                )}

                {!showRegular && showDaypass && (
                  <>
                    <p className="text-xs text-green-600 mb-1">
                      📈 เฉลี่ย:{" "}
                      {formatCurrency(
                        Math.round(totalDaypassRevenue / yearData.length),
                      )}
                      /วัน
                    </p>
                    {maxDay && (
                      <div className="text-xs text-emerald-700 mb-1">
                        🔝 สูงสุด:{" "}
                        {formatCurrency(Math.round(maxDay.daypassRevenue))}
                        <br />
                        <span className="text-gray-500 ml-3">
                          ({maxDay.date})
                        </span>
                      </div>
                    )}
                    {minDay && (
                      <div className="text-xs text-teal-600">
                        🔻 ต่ำสุด:{" "}
                        {formatCurrency(Math.round(minDay.daypassRevenue))}
                        <br />
                        <span className="text-gray-500 ml-3">
                          ({minDay.date})
                        </span>
                      </div>
                    )}
                  </>
                )}

                <p className="text-xs text-gray-500 mt-2 pt-2 border-t">
                  📅 {yearData.length} วัน
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Calculate statistics for single view
  const totalRevenue = timelineData.reduce(
    (sum, item) => sum + item.revenue,
    0,
  );
  const totalDaypassRevenue = timelineData.reduce(
    (sum, item) => sum + item.daypassRevenue,
    0,
  );
  const grandTotal = timelineData.reduce((sum, item) => sum + item.total, 0);
  const avgTotal =
    timelineData.length > 0 ? grandTotal / timelineData.length : 0;
  const avgRevenue =
    timelineData.length > 0 ? totalRevenue / timelineData.length : 0;
  const avgDaypassRevenue =
    timelineData.length > 0 ? totalDaypassRevenue / timelineData.length : 0;

  // Render single timeline view (no year comparison)
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 border-2 border-green-100 hover:border-green-200 transition-all duration-300">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
              กราฟ Timeline รายรับรายวัน
              <span className="px-2 py-0.5 text-xs font-bold bg-green-200 text-green-700 rounded-full">
                {timelineData.length} วัน
              </span>
            </h3>
            <p className="text-sm text-green-600">
              {!filters?.startDate && !filters?.endDate
                ? "แสดงข้อมูลทั้งหมด"
                : `ช่วงวันที่: ${filters.startDate} ถึง ${filters.endDate}`}
            </p>
          </div>

          {/* Toggle Buttons for Data Types */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowRegular(!showRegular)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                showRegular
                  ? "text-white shadow-lg"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
              style={showRegular ? { background: "#22c55e" } : {}}
            >
              {showRegular ? "✓" : ""} ตั๋วฉีก
            </button>
            <button
              onClick={() => setShowDaypass(!showDaypass)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                showDaypass
                  ? "text-white shadow-lg"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
              style={showDaypass ? { background: "#14b8a6" } : {}}
            >
              {showDaypass ? "✓" : ""} Daypass
            </button>
          </div>
        </div>

        {/* Year Selection */}
        {availableYears.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableYears.map((year, index) => (
              <button
                key={year}
                onClick={() => handleYearToggle(year)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  selectedYears.includes(year)
                    ? "text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                style={
                  selectedYears.includes(year)
                    ? { background: YEAR_COLORS[index % YEAR_COLORS.length] }
                    : {}
                }
              >
                {year} {selectedYears.includes(year) && "✓"}
              </button>
            ))}
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={timelineData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            tick={{ fill: "#666", fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tick={{ fill: "#666", fontSize: 12 }} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <div className="bg-white p-4 border-2 border-green-200 rounded-lg shadow-lg">
                    <p className="font-semibold text-gray-800 mb-2">{label}</p>
                    <p className="text-green-600">
                      ตั๋วฉีก: {formatCurrency(data.revenue)}
                    </p>
                    <p className="text-emerald-600">
                      Daypass: {formatCurrency(data.daypassRevenue)}
                    </p>
                    <p className="text-green-700 font-bold mt-1">
                      รวมทั้งหมด: {formatCurrency(data.total)}
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend wrapperStyle={{ paddingTop: "20px" }} />

          {showRegular && showDaypass && (
            <Line
              type="monotone"
              dataKey="total"
              name="รายรับรวมทั้งหมด (ตั๋วฉีก + Daypass)"
              stroke="#10b981"
              strokeWidth={3}
              dot={{ r: 2, fill: "#10b981" }}
              activeDot={{ r: 4 }}
            />
          )}

          {showRegular && !showDaypass && (
            <Line
              type="monotone"
              dataKey="revenue"
              name="รายรับ (ตั๋วฉีก)"
              stroke="#22c55e"
              strokeWidth={3}
              dot={{ r: 2, fill: "#22c55e" }}
              activeDot={{ r: 4 }}
            />
          )}

          {!showRegular && showDaypass && (
            <Line
              type="monotone"
              dataKey="daypassRevenue"
              name="รายรับ (Daypass)"
              stroke="#14b8a6"
              strokeWidth={3}
              dot={{ r: 2, fill: "#14b8a6" }}
              activeDot={{ r: 4 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border-2 border-green-100">
          <p className="text-sm text-gray-600 mb-1">รวมทั้งหมด (เฉลี่ย)</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(Math.round(avgTotal))}
          </p>
          <p className="text-xs text-gray-500">/วัน</p>
        </div>

        <div className="bg-white rounded-xl p-4 border-2 border-green-100">
          <p className="text-sm text-gray-600 mb-1">ตั๋วฉีก (เฉลี่ย)</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(Math.round(avgRevenue))}
          </p>
          <p className="text-xs text-gray-500">/วัน</p>
        </div>

        <div className="bg-white rounded-xl p-4 border-2 border-emerald-100">
          <p className="text-sm text-gray-600 mb-1">Daypass (เฉลี่ย)</p>
          <p className="text-2xl font-bold text-emerald-600">
            {formatCurrency(Math.round(avgDaypassRevenue))}
          </p>
          <p className="text-xs text-gray-500">/วัน</p>
        </div>

        <div className="bg-white rounded-xl p-4 border-2 border-teal-100">
          <p className="text-sm text-gray-600 mb-1">จำนวนวันทั้งหมด</p>
          <p className="text-2xl font-bold text-teal-600">
            {formatNumber(timelineData.length)}
          </p>
          <p className="text-xs text-gray-500">วัน</p>
        </div>
      </div>

      {/* Total Summary */}
      <div className="mt-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-700 mb-1">รายรับรวม (ตั๋วฉีก)</p>
            <p className="text-xl font-bold text-green-700">
              {formatCurrency(totalRevenue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-700 mb-1">รายรับรวม (Daypass)</p>
            <p className="text-xl font-bold text-emerald-700">
              {formatCurrency(totalDaypassRevenue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-700 mb-1">รายรับรวมทั้งหมด</p>
            <p className="text-xl font-bold text-green-800">
              {formatCurrency(grandTotal)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CombinedRevenueTimelineChart;
